import React, { useMemo } from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { compact } from 'lodash'
import { useNavigation } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@src/components/base'
import {
  priorityOrderedCoachAssignmentsSelector,
  topMemberNotesSelector,
} from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { MemberNoteCard } from '@src/screens/NutritionistHub/components/MemberNotes/MemberNoteCard'
import { useNotesUpdateInfo } from '@src/screens/NutritionistHub/hooks/nutritionistNotes/useNotesUpdateInfo'
import { CustomEventTypes } from '@src/config'
import { useNutritionistHubTracker } from '@src/screens/NutritionistHub/hooks'
import { NoNotesPlaceholder } from '@src/screens/NutritionistHub/components/MemberNotes/NoNotesPlaceholder'

export const MemberTopNotes = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation()
  const track = useNutritionistHubTracker()

  const topNotes = compact(useSelector(topMemberNotesSelector))
  const allCoachAssignments = useSelector(priorityOrderedCoachAssignmentsSelector)
  const { coachName, coachKind, updatedLast } = useNotesUpdateInfo(topNotes)
  const noNotes = topNotes.length === 0

  const updateInfo = !noNotes && (
    <Text type="small" style={styles.subtitle}>
      {`Updated ${updatedLast} by ${coachKind || coachName}`}
    </Text>
  )

  const handleSeeAllPress = () => {
    track(CustomEventTypes.NutritionistHubSeeAllNotesTapped)
    navigation.navigate('MemberNotes')
  }

  const seeAllButton = !noNotes && (
    <Button
      accessibilityLabel="seeAllNotes"
      type="outline"
      size="small"
      style={styles.seeAllButton}
      onPress={handleSeeAllPress}
    >
      See All
    </Button>
  )

  const noNotesPlaceholder = noNotes && <NoNotesPlaceholder />

  const notesWithCoachAssignment = useMemo(() => {
    const coachAssignments = allCoachAssignments ?? []

    return topNotes.map((note) => ({
      note,
      coachAssignment: coachAssignments.find(
        (assignment) => assignment.coach?.id === note.creator.id,
      ),
    }))
  }, [allCoachAssignments, topNotes])

  return (
    <View style={styles.container}>
      <View style={styles.sectionHeader}>
        <View style={styles.header}>
          <Icon name="notepad" />
          <View style={styles.headerText}>
            <Text type="regular" bold>
              Notes from Care Team
            </Text>
            {updateInfo}
          </View>
        </View>
        {seeAllButton}
      </View>
      <View style={styles.notesContainer}>
        {noNotesPlaceholder}
        {notesWithCoachAssignment.map(({ note, coachAssignment }) => (
          <MemberNoteCard
            key={note.id}
            note={note}
            coachAssignment={coachAssignment}
            showLatestTag
          />
        ))}
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    marginBottom: 24,
  },
  sectionHeader: {
    marginTop: 24,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  header: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    marginLeft: 8,
    lineHeight: 24,
    flex: 1,
    flexDirection: 'column',
  },
  subtitle: {
    color: 'theme.text.secondary',
  },
  seeAllButton: {
    alignSelf: 'center',
  },
  notesContainer: {
    rowGap: 8,
  },
})
